import { collection, getDocs, query, where } from 'firebase/firestore';
import { enrollmentKind, isCurrentEnrollment, type EnrollmentLike } from '@/lib/enrollment-kind';
import { db, getSellerSalesCallable } from '@/lib/firebase';
import {
  CourseRepository,
  LessonRepository,
  QuizRepository,
  UserRepository,
  AnalyticsRepository
} from '../repository';

import {
  CertificationUseCase,
  QuizUseCase,
  AnalyticsUseCase
} from '../usecases';

/**
 * Dedicated Courses Service
 * Handles all course-related operations
 */
/**
 * Lessons hang off modules and quizzes off lessons (the editors write no
 * courseId on either), so counting "by course" found none -- every course
 * showed 0 lessons, 0 quizzes. Walk course -> modules -> lessons -> quizzes,
 * unordered: the repositories' ordered queries drop documents that lack the
 * sort field.
 */
async function countCourseContent(courseId: string): Promise<{ lessonCount: number; quizCount: number }> {
  const inChunks = async (col: string, field: string, ids: string[]) => {
    const out: string[] = [];
    for (let i = 0; i < ids.length; i += 30) {
      const snap = await getDocs(query(collection(db, col), where(field, 'in', ids.slice(i, i + 30))));
      snap.forEach((d) => out.push(d.id));
    }
    return out;
  };
  const moduleIds = (await getDocs(query(collection(db, 'modules'), where('courseId', '==', courseId)))).docs.map((d) => d.id);
  const lessonIds = await inChunks('lessons', 'moduleId', moduleIds);
  const quizIds = new Set<string>([
    ...(await getDocs(query(collection(db, 'quizzes'), where('courseId', '==', courseId)))).docs.map((d) => d.id),
    ...(await inChunks('quizzes', 'moduleId', moduleIds)),
    ...(await inChunks('quizzes', 'lessonId', lessonIds)),
  ]);
  return { lessonCount: lessonIds.length, quizCount: quizIds.size };
}

export class CoursesService {
  constructor(
    private courseRepo: CourseRepository,
    private lessonRepo: LessonRepository,
    private quizRepo: QuizRepository,
    private userRepo: UserRepository,
    private analyticsRepo: AnalyticsRepository,
    private certificationUseCase: CertificationUseCase,
    private quizUseCase: QuizUseCase,
    private analyticsUseCase: AnalyticsUseCase
  ) {}

  // ===== COURSE CRUD OPERATIONS =====

  /**
   * Get all courses
   */
  async getAllCourses(limit?: number) {
    console.log('[CoursesService.getAllCourses] START - Limit:', limit);
    try {
      console.log('[CoursesService.getAllCourses] Calling courseRepo.getCourses');
      const result = await this.courseRepo.getCourses({}, limit);
      console.log('[CoursesService.getAllCourses] SUCCESS - Retrieved', result.length, 'courses');
      return result;
    } catch (error) {
      console.error('[CoursesService.getAllCourses] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get course by ID
   */
  async getCourseById(courseId: string) {
    console.log('[CoursesService.getCourseById] START - CourseId:', courseId);
    try {
      console.log('[CoursesService.getCourseById] Calling courseRepo.getCourseById');
      const result = await this.courseRepo.getCourseById(courseId);
      console.log('[CoursesService.getCourseById] SUCCESS - Course found:', result ? 'Yes' : 'No');
      return result;
    } catch (error) {
      console.error('[CoursesService.getCourseById] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get courses by instructor ID (THIS IS THE KEY METHOD!)
   */
  async getCoursesByInstructor(instructorId: string, limit?: number) {
    console.log('[CoursesService.getCoursesByInstructor] START - InstructorId:', instructorId, 'Limit:', limit);
    try {
      console.log('[CoursesService.getCoursesByInstructor] Calling courseRepo.getCoursesByInstructor');
      const result = await this.courseRepo.getCoursesByInstructor(instructorId, limit);
      console.log('[CoursesService.getCoursesByInstructor] SUCCESS - Found', result.length, 'courses for instructor:', instructorId);
      return result;
    } catch (error) {
      console.error('[CoursesService.getCoursesByInstructor] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get courses by owner (alias for getCoursesByInstructor)
   */
  async getCoursesByOwner(ownerId: string, limit?: number) {
    console.log('[CoursesService.getCoursesByOwner] START - OwnerId:', ownerId, 'Limit:', limit);
    try {
      console.log('[CoursesService.getCoursesByOwner] Delegating to getCoursesByInstructor');
      const result = await this.getCoursesByInstructor(ownerId, limit);
      console.log('[CoursesService.getCoursesByOwner] SUCCESS - Found', result.length, 'courses for owner:', ownerId);
      return result;
    } catch (error) {
      console.error('[CoursesService.getCoursesByOwner] ERROR:', error);
      throw error;
    }
  }

  /**
   * Create a new course
   */
  async createCourse(courseData: any) {
    console.log('[CoursesService.createCourse] START - CourseData:', courseData);
    try {
      console.log('[CoursesService.createCourse] Calling courseRepo.createCourse');
      const result = await this.courseRepo.createCourse(courseData);
      console.log('[CoursesService.createCourse] SUCCESS - Created course:', result.id);
      return result;
    } catch (error) {
      console.error('[CoursesService.createCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Update a course
   */
  async updateCourse(courseId: string, updateData: any) {
    console.log('[CoursesService.updateCourse] START - CourseId:', courseId, 'UpdateData:', updateData);
    try {
      console.log('[CoursesService.updateCourse] Calling courseRepo.updateCourse');
      const result = await this.courseRepo.updateCourse(courseId, updateData);
      console.log('[CoursesService.updateCourse] SUCCESS - Updated course:', courseId);
      return result;
    } catch (error) {
      console.error('[CoursesService.updateCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Delete a course
   */
  async deleteCourse(courseId: string) {
    console.log('[CoursesService.deleteCourse] START - CourseId:', courseId);
    try {
      console.log('[CoursesService.deleteCourse] Calling courseRepo.deleteCourse');
      await this.courseRepo.deleteCourse(courseId);
      console.log('[CoursesService.deleteCourse] SUCCESS - Deleted course:', courseId);
    } catch (error) {
      console.error('[CoursesService.deleteCourse] ERROR:', error);
      throw error;
    }
  }

  // ===== COURSE CONTENT MANAGEMENT =====

  /**
   * Get lessons for a course
   */
  async getLessonsByCourse(courseId: string) {
    console.log('[CoursesService.getLessonsByCourse] START - CourseId:', courseId);
    try {
      console.log('[CoursesService.getLessonsByCourse] Calling lessonRepo.getLessonsByOwner');
      const result = await this.lessonRepo.getLessonsByOwner(courseId);
      console.log('[CoursesService.getLessonsByCourse] SUCCESS - Found', result.length, 'lessons for course:', courseId);
      return result;
    } catch (error) {
      console.error('[CoursesService.getLessonsByCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get quizzes for a course
   */
  async getQuizzesByCourse(courseId: string) {
    console.log('[CoursesService.getQuizzesByCourse] START - CourseId:', courseId);
    try {
      console.log('[CoursesService.getQuizzesByCourse] Calling quizRepo.getQuizzesByOwner');
      const result = await this.quizRepo.getQuizzesByOwner(courseId);
      console.log('[CoursesService.getQuizzesByCourse] SUCCESS - Found', result.length, 'quizzes for course:', courseId);
      return result;
    } catch (error) {
      console.error('[CoursesService.getQuizzesByCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get course with all its content (lessons + quizzes)
   */
  async getCourseWithContent(courseId: string) {
    console.log('[CoursesService.getCourseWithContent] START - CourseId:', courseId);
    try {
      console.log('[CoursesService.getCourseWithContent] Getting course and its content');
      
      const [course, lessons, quizzes] = await Promise.all([
        this.getCourseById(courseId),
        this.getLessonsByCourse(courseId),
        this.getQuizzesByCourse(courseId)
      ]);

      if (!course) {
        throw new Error(`Course not found: ${courseId}`);
      }

      console.log('[CoursesService.getCourseWithContent] SUCCESS - Course with', lessons.length, 'lessons and', quizzes.length, 'quizzes');
      return {
        ...course,
        lessons,
        quizzes,
        contentCount: lessons.length + quizzes.length
      };
    } catch (error) {
      console.error('[CoursesService.getCourseWithContent] ERROR:', error);
      throw error;
    }
  }

  // ===== ENROLLMENT MANAGEMENT =====

  /**
   * Get course enrollments
   */
  async getCourseEnrollments(courseId: string) {
    console.log('[CoursesService.getCourseEnrollments] START - CourseId:', courseId);
    try {
      console.log('[CoursesService.getCourseEnrollments] Calling courseRepo.getCourseEnrollments');
      const result = await this.courseRepo.getCourseEnrollments(courseId);
      console.log('[CoursesService.getCourseEnrollments] SUCCESS - Found', result.length, 'enrollments for course:', courseId);
      return result;
    } catch (error) {
      console.error('[CoursesService.getCourseEnrollments] ERROR:', error);
      throw error;
    }
  }

  /**
   * Enroll user in course
   */
  async enrollUserInCourse(userId: string, courseId: string) {
    console.log('[CoursesService.enrollUserInCourse] START - UserId:', userId, 'CourseId:', courseId);
    try {
      console.log('[CoursesService.enrollUserInCourse] Calling courseRepo.enrollUser');
      const result = await this.courseRepo.enrollUser({
        userId: userId,
        courseId: courseId,
        enrollmentStatus: 'enrolled'
      });
      console.log('[CoursesService.enrollUserInCourse] SUCCESS - Enrolled user in course');
      return result;
    } catch (error) {
      console.error('[CoursesService.enrollUserInCourse] ERROR:', error);
      throw error;
    }
  }

  // ===== INSTRUCTOR DASHBOARD DATA =====

  /**
   * Get instructor dashboard data
   * This is the main method for instructor course management
   */
  async getInstructorDashboardData(instructorId: string) {
    console.log('[CoursesService.getInstructorDashboardData] START - InstructorId:', instructorId);
    try {
      console.log('[CoursesService.getInstructorDashboardData] Getting comprehensive instructor data');

      // Get instructor's courses
      const courses = await this.getCoursesByInstructor(instructorId);
      console.log('[CoursesService.getInstructorDashboardData] Found', courses.length, 'courses');

      // Revenue is what was actually paid, from the same sale rows the
      // payout uses -- not enrollments x list price, which counted comped
      // students as income and moved with every price change.
      const salesResult = await getSellerSalesCallable().catch(() => null);
      const sales = ((salesResult?.data as { sales?: Array<Record<string, unknown>> } | undefined)?.sales ?? [])
        .filter((s) => s.itemType === 'course' && s.settled === true && s.refunded !== true);

      const coursesWithEnrollments = await Promise.all(
        courses.map(async (course) => {
          // Current students only. A refund sets paymentStatus, not status
          // (which stays COMPLETED), so the old status-only filter counted
          // refunded learners as students.
          const all = await this.getCourseEnrollments(course.id);
          const kinds = { paid: 0, free: 0, added: 0, refunded: 0, disputed: 0, revoked: 0 };
          for (const e of all) kinds[enrollmentKind(e as unknown as EnrollmentLike)]++;
          const enrollments = all.filter((e) => isCurrentEnrollment(e as unknown as EnrollmentLike));
          const { lessonCount, quizCount } = await countCourseContent(course.id);
          const courseSales = sales.filter((s) => s.courseId === course.id);
          return {
            ...course,
            enrollmentCount: enrollments.length,
            enrollmentKinds: kinds,
            studentIds: enrollments.map((e) => String((e as unknown as { userId?: string }).userId ?? '')).filter(Boolean),
            lessonCount,
            quizCount,
            contentCount: lessonCount + quizCount,
            salesCount: courseSales.length,
            revenue: courseSales.reduce((sum, s) => sum + Number(s.gross || 0), 0),
          };
        })
      );

      // Calculate summary stats
      const totalEnrollments = coursesWithEnrollments.reduce((sum, course) => sum + course.enrollmentCount, 0);
      const totalRevenue = coursesWithEnrollments.reduce((sum, course) => sum + course.revenue, 0);
      const avgRating = courses.length > 0 ? 
        courses.reduce((sum, course) => sum + (course.averageRating || 0), 0) / courses.length : 0;

      console.log('[CoursesService.getInstructorDashboardData] SUCCESS - Compiled data for', courses.length, 'courses');
      return {
        courses: coursesWithEnrollments,
        stats: {
          totalCourses: courses.length,
          totalEnrollments,
          // One learner in two courses is one student, two enrollments.
          uniqueStudents: new Set(coursesWithEnrollments.flatMap((c) => c.studentIds)).size,
          totalRevenue,
          averageRating: avgRating
        }
      };
    } catch (error) {
      console.error('[CoursesService.getInstructorDashboardData] ERROR:', error);
      throw error;
    }
  }
}
